import { useState } from 'react';
import { useQuery } from '@apollo/client/react';

import textConstants from '@atom/constants/textConstants';
import { GET_ASSET } from '@atom/graph/asset';
import { GET_WORK_ORDERS } from '@atom/graph/work';
import { Snackbar } from '@atom/mui';
import { AssetDetail } from '@atom/types/asset';
import { AttributesType } from '@atom/types/schema';
import { WorkOrder } from '@atom/types/work';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';

export type AttributesSavePayload = {
  id: string;
  attributeGroupName: string;
  attributes: Record<string, AttributesType>;
};

export type AssetSidebarData = {
  asset: AssetDetail | undefined;
  workOrders: WorkOrder[];
  loading: boolean;
  error: boolean;
  loadingRename: boolean;
  loadingApprove: boolean;
  attributeLoading: string[];
  renameAsset: (name: string) => Promise<void>;
  approveAllChanges: () => Promise<boolean>;
  onAttributesPendingChange: (
    action: string,
    attributeGroupName: string,
    attribute: AttributesType,
  ) => Promise<void>;
  onAttributesSave: (payload: AttributesSavePayload) => Promise<void>;
  refetch: () => Promise<unknown>;
};

const useAssetSidebarData = (id: string): AssetSidebarData => {
  const [loadingRename, setLoadingRename] = useState(false);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [attributeLoading, setAttributeLoading] = useState<string[]>([]);

  const {
    loading: assetLoading,
    data: assetData,
    error: assetError,
    refetch,
  } = useQuery(GET_ASSET, {
    variables: { id },
    fetchPolicy: 'no-cache',
    skip: !id,
  });

  const { loading: workOrdersLoading, data: workOrdersData } = useQuery(
    GET_WORK_ORDERS,
    {
      variables: {
        input: {
          assetId: id,
          statusIds: [0, 1, 2, 3, 4, 5],
          sortBy: 'status',
          page: 1,
          limit: 250,
        },
      },
      fetchPolicy: 'no-cache',
      skip: !id,
    },
  );

  const renameAsset = async (name: string) => {
    setLoadingRename(true);
    try {
      await api.patch(`${INVENTORY_ASSETS_ENDPOINT}/${id}`, { name });
      await refetch();
    } finally {
      setLoadingRename(false);
    }
  };

  const approveAllChanges = async (): Promise<boolean> => {
    setLoadingApprove(true);
    try {
      await api.post(`${INVENTORY_ASSETS_ENDPOINT}/${id}/approve`, {
        includeChangesBefore: assetData?.asset?.includeChangesBefore,
        rootAssetId: id,
      });
      await refetch();
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
      return true;
    } catch {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
      return false;
    } finally {
      setLoadingApprove(false);
    }
  };

  const onAttributesPendingChange = async (
    action: string,
    _attributeGroupName: string,
    attribute: AttributesType,
  ) => {
    try {
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATING_TEXT,
      });
      await api.post(
        `${INVENTORY_ASSETS_ENDPOINT}/${id}/attributes/${attribute.id}/changes/${attribute.changeId}/${action}`,
        { rootAssetId: id },
      );
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
      await refetch();
    } catch {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
  };

  const onAttributesSave = async (payload: AttributesSavePayload) => {
    setAttributeLoading([payload.attributeGroupName]);
    try {
      await api.patch(
        `${INVENTORY_ASSETS_ENDPOINT}/${id}/attributes`,
        payload.attributes,
      );
      await refetch();
      Snackbar.info({
        message: textConstants.GENERIC_APPLICATION_UPDATED_TEXT,
        autoHideDuration: 3000,
      });
    } catch {
      Snackbar.error({
        message: textConstants.GENERIC_APPLICATION_ERROR_TEXT,
      });
    }
    setAttributeLoading([]);
  };

  const loading =
    (assetLoading && !assetData) || (workOrdersLoading && !workOrdersData);
  const error = Boolean(assetError);

  return {
    asset: assetData?.asset,
    workOrders: (workOrdersData?.workOrders?.workOrders ?? []) as WorkOrder[],
    loading,
    error,
    loadingRename,
    loadingApprove,
    attributeLoading,
    renameAsset,
    approveAllChanges,
    onAttributesPendingChange,
    onAttributesSave,
    refetch,
  };
};

export default useAssetSidebarData;
